import { useEffect, useMemo, useState } from "react";
import { Link } from "react-router-dom";
import { api } from "../api";
import { useI18n } from "../i18n";
import { Clock, Filter, ArrowRight } from "lucide-react";
import SEO from "../components/SEO";

const REGION_LABELS = {
  lombardia: "Lombardia", veneto: "Veneto", "emilia-romagna": "Emilia-Romagna",
  piemonte: "Piemonte", toscana: "Toscana",
};

const TOPIC_LABELS = {
  valutazione: "Valutazione", successione: "Successione", fisco: "Fisco",
  banche: "Banche & Debito", metodo: "Metodo", governance: "Governance",
  "sale-ready": "Sale-Ready", meccanica: "Meccanica", alimentare: "Alimentare",
  distretti: "Distretti", multipli: "Multipli",
};

export default function InsightsIndex() {
  const { lang } = useI18n();
  const [insights, setInsights] = useState([]);
  const [selectedTag, setSelectedTag] = useState(null);
  const [selectedRegion, setSelectedRegion] = useState(null);

  useEffect(() => {
    const q = new URLSearchParams();
    if (selectedTag) q.set("tag", selectedTag);
    if (selectedRegion) q.set("region_tag", selectedRegion);
    q.set("category", "insight");
    api.get(`/insights?${q.toString()}`).then(r => setInsights(r.data || []));
  }, [selectedTag, selectedRegion]);

  const allTags = useMemo(() => {
    const s = new Set();
    insights.forEach(i => (i.tags || []).forEach(t => s.add(t)));
    return [...s];
  }, [insights]);

  return (
    <main data-testid="insights-index">
      <SEO
        title={lang === "it" ? "Editoriale · Successione, valutazione, M&A per PMI italiane" : "Editorial · Succession, valuation, M&A for Italian SMEs"}
        description={lang === "it"
          ? "Articoli settimanali del team GIANO su successione, valutazione, capacità di debito, distretti industriali e fiscalità della cessione. Analisi rigorose per gli imprenditori delle PMI italiane."
          : "Weekly articles by the GIANO team on succession, valuation, debt capacity, industrial districts and sale taxation for Italian SME owners."}
        path="/insights"
        jsonLd={{
          "@context": "https://schema.org",
          "@type": "Blog",
          "name": "GIANO Editoriale",
          "url": "https://gianoholding.com/insights",
          "inLanguage": lang === "it" ? "it-IT" : "en-US",
          "publisher": { "@id": "https://gianoholding.com/#organization" },
          "blogPost": insights.slice(0, 10).map(i => ({
            "@type": "BlogPosting",
            "headline": lang === "it" ? i.title_it : i.title_en,
            "url": `https://gianoholding.com/insights/${i.slug}`,
            "datePublished": i.published_at,
            "author": { "@type": "Organization", "name": "GIANO" },
          })),
        }}
      />

      <section style={{ background: "var(--midnight)", color: "var(--bone)" }} className="py-20 md:py-24">
        <div className="container-x">
          <div className="overline" style={{ color: "var(--gold)" }}>Editoriale · Team M&amp;A GIANO</div>
          <h1 className="serif-display mt-6" style={{ fontSize: "clamp(40px, 5.5vw, 76px)", lineHeight: 1.05, maxWidth: 1100 }}>
            Numeri, non narrativa.<br />
            <span style={{ color: "var(--gold)", fontStyle: "italic" }}>Sulle PMI italiane in transizione.</span>
          </h1>
          <p className="mt-8 text-lg md:text-xl" style={{ maxWidth: 820, opacity: 0.85, lineHeight: 1.7 }}>
            Un articolo alla settimana. Valutazione, capacità di debito, fiscalità della cessione, distretti industriali,
            metodo. Per titolari che valutano una successione entro 3-5 anni e vogliono capire prima di essere costretti a scegliere.
          </p>
        </div>
      </section>

      <section className="py-10" style={{ background: "var(--muted)" }}>
        <div className="container-x">
          <div className="flex items-center gap-3 flex-wrap">
            <Filter size={14} style={{ color: "var(--terracotta)" }} />
            <span className="font-mono text-xs" style={{ opacity: 0.6, letterSpacing: 1 }}>ARGOMENTO</span>
            <FilterPill active={!selectedTag} onClick={() => setSelectedTag(null)} label="Tutti" />
            {allTags.map(t => (
              <FilterPill key={t} active={selectedTag === t} onClick={() => setSelectedTag(t)} label={TOPIC_LABELS[t] || t} />
            ))}
          </div>
          <div className="flex items-center gap-3 flex-wrap mt-4">
            <Filter size={14} style={{ color: "var(--terracotta)" }} />
            <span className="font-mono text-xs" style={{ opacity: 0.6, letterSpacing: 1 }}>REGIONE</span>
            <FilterPill active={!selectedRegion} onClick={() => setSelectedRegion(null)} label="Tutte" />
            {Object.entries(REGION_LABELS).map(([slug, label]) => (
              <FilterPill key={slug} active={selectedRegion === slug} onClick={() => setSelectedRegion(slug)} label={label} />
            ))}
          </div>
        </div>
      </section>

      <section className="py-16" style={{ background: "var(--bone)" }}>
        <div className="container-x">
          {insights.length === 0 ? (
            <div className="py-24 text-center text-sm" style={{ opacity: 0.6 }}>Nessun articolo per i filtri selezionati.</div>
          ) : (
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-8">
              {insights.map(i => (
                <ArticleCard key={i.id} insight={i} lang={lang} />
              ))}
            </div>
          )}
        </div>
      </section>
    </main>
  );
}

function FilterPill({ active, onClick, label }) {
  return (
    <button
      onClick={onClick}
      data-testid={`filter-pill-${label.toLowerCase().replace(/[^a-z]/g, "-")}`}
      className="font-mono text-xs px-3 py-1"
      style={{
        border: `1px solid ${active ? "var(--midnight)" : "var(--border)"}`,
        background: active ? "var(--midnight)" : "transparent",
        color: active ? "var(--bone)" : "var(--midnight)",
        cursor: "pointer",
        letterSpacing: 0.5,
      }}
    >
      {label}
    </button>
  );
}

export function ArticleCard({ insight, lang, compact }) {
  const title = lang === "it" ? insight.title_it : insight.title_en;
  const excerpt = lang === "it" ? insight.excerpt_it : insight.excerpt_en;
  const date = new Date(insight.published_at).toLocaleDateString(lang === "it" ? "it-IT" : "en-US", { year: "numeric", month: "short", day: "numeric" });

  return (
    <Link
      to={`/insights/${insight.slug}`}
      data-testid={`article-card-${insight.slug}`}
      className="card-flat block p-6 md:p-7 hover:shadow-lg transition-shadow"
      style={{ background: "var(--card)", border: "1px solid var(--border)", textDecoration: "none", color: "inherit" }}
    >
      <div className="flex items-center gap-2 font-mono text-xs" style={{ color: "var(--terracotta)", letterSpacing: 1 }}>
        {(insight.tags || []).slice(0, 2).map(t => (
          <span key={t}>{(TOPIC_LABELS[t] || t).toUpperCase()}</span>
        ))}
      </div>
      <h3 className="serif-display mt-4" style={{ fontSize: compact ? 20 : 24, lineHeight: 1.25 }}>
        {title}
      </h3>
      {!compact && (
        <p className="mt-4 text-sm" style={{ opacity: 0.78, lineHeight: 1.65 }}>{excerpt}</p>
      )}
      <div className="mt-5 pt-4 flex items-center justify-between font-mono text-xs" style={{ borderTop: "1px solid var(--border)", opacity: 0.65 }}>
        <span>{date}</span>
        <span className="flex items-center gap-1"><Clock size={11} /> {insight.reading_minutes || 6} min</span>
      </div>
      <div className="mt-4 flex items-center gap-1 text-sm font-mono" style={{ color: "var(--terracotta)" }}>
        Leggi <ArrowRight size={12} />
      </div>
    </Link>
  );
}
